import { z } from 'zod'
import type { TafTemperature } from './tafDecoder'

// Provider-independent weather products. Never merge them: a nearby
// station's readings must not be presented as a METAR.

export const ICAO_CODE = /^[A-Z]{4}$/
export const IcaoParamSchema = z.object({
  icao: z
    .string()
    .transform((s) => s.toUpperCase())
    .pipe(z.string().regex(ICAO_CODE)),
})

export type WeatherProviderId = 'aviationweather' | 'fmi'
export type WeatherProduct = 'metar' | 'taf' | 'localStation' | 'pointForecast'

export interface WeatherFreshness {
  retrievedAt: string
  // The last refresh failed; this is the previous good copy.
  stale: boolean
  staleSince: string | null
}

export type CloudAmount = 'FEW' | 'SCT' | 'BKN' | 'OVC' | 'VV' | 'NSC' | 'NCD' | 'SKC' | 'CLR'

export interface CloudLayer {
  amount: CloudAmount
  baseFt: number | null
  // CB / TCU.
  type: string | null
}

export interface MetarWind {
  // null when VRB.
  directionDeg: number | null
  speedKt: number
  gustKt: number | null
  // The dddVddd group.
  variableFromDeg: number | null
  variableToDeg: number | null
}

export type FlightCategory = 'VFR' | 'MVFR' | 'IFR' | 'LIFR'

export interface MetarObservation extends WeatherFreshness {
  provider: WeatherProviderId
  station: string
  observedAt: string
  raw: string
  wind: MetarWind | null
  // 9999: 10 km or more.
  visibilityM: number | null
  cavok: boolean
  temperatureC: number | null
  dewPointC: number | null
  qnhHpa: number | null
  clouds: CloudLayer[]
  weather: string[]
  flightCategory: FlightCategory | null
}

export type TafChange = 'FM' | 'BECMG' | 'TEMPO' | 'PROB'

export interface TafPeriod {
  from: string
  to: string
  change: TafChange | null
  probability: number | null
  becomingBy: string | null
  wind: { directionDeg: number | null; speedKt: number; gustKt: number | null } | null
  visibilityM: number | null
  verticalVisibilityFt: number | null
  cavok: boolean
  // NSW: earlier weather ends.
  noSignificantWeather: boolean
  clouds: CloudLayer[]
  weather: string[]
}

export interface TafForecast extends WeatherFreshness {
  provider: WeatherProviderId
  station: string
  issuedAt: string
  validFrom: string
  validTo: string
  raw: string
  amendment: 'AMD' | 'COR' | null
  periods: TafPeriod[]
  temperatures: TafTemperature[]
}

export interface WeatherStationInfo {
  id: string
  name: string
  lat: number
  lon: number
  // From the aerodrome reference point.
  distanceKm: number
  bearingDeg: number
}

// Not an aviation observation; pressure is sea-level pressure, not QNH.
export interface StationObservation extends WeatherFreshness {
  provider: WeatherProviderId
  station: WeatherStationInfo
  observedAt: string
  temperatureC: number | null
  dewPointC: number | null
  pressureHpa: number | null
  windDirectionDeg: number | null
  windSpeedKt: number | null
  windGustKt: number | null
  visibilityM: number | null
  cloudCoverOktas: number | null
  precipitation1hMm: number | null
}

export interface PointForecastHour {
  time: string
  temperatureC: number | null
  dewPointC: number | null
  windDirectionDeg: number | null
  windSpeedKt: number | null
  gustKt: number | null
  cloudCoverOktas: number | null
  precipitationMm: number | null
}

// Numerical point forecast for the aerodrome; not a TAF.
export interface PointForecast extends WeatherFreshness {
  provider: WeatherProviderId
  hours: PointForecastHour[]
}

export interface WeatherAirport {
  icao: string
  name: string
  lat: number
  lon: number
  // Products with a source here: tells "none issued" from "unavailable".
  products: WeatherProduct[]
}

export interface AirportWeather {
  airport: WeatherAirport
  metar: MetarObservation | null
  taf: TafForecast | null
  localStation: StationObservation | null
  pointForecast: PointForecast | null
}

export const KT_PER_MS = 1.943844

export const percentToOktas = (percent: number) => Math.round((Math.min(100, Math.max(0, percent)) / 100) * 8)

// Never negative: a clock slightly ahead must not read "-1 min".
export function ageMinutes(iso: string, now: number): number {
  return Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000))
}
